import type {
  CopyFaqBundle,
  CopyFaqPayload,
  CopyFaqResponse,
  EventCopy,
  FaqDraft,
  FaqEntry,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value?: unknown) {
  const num = Number(value ?? 0);

  return Number.isFinite(num) ? num : 0;
}

export function getDefaultCopy(): EventCopy {
  return {
    bannerZh: '',
    bannerEn: '',
    taglineZh: '',
    taglineEn: '',
  };
}

export function getDefaultBundle(): CopyFaqBundle {
  return {
    copy: getDefaultCopy(),
    faqs: [],
  };
}

export function normalizeCopy(raw: unknown): EventCopy {
  const fallback = getDefaultCopy();

  if (!isRecord(raw)) {
    return fallback;
  }

  return {
    bannerZh: String(raw.bannerZh ?? raw.banner_zh ?? fallback.bannerZh),
    bannerEn: String(raw.bannerEn ?? raw.banner_en ?? fallback.bannerEn),
    taglineZh: String(raw.taglineZh ?? raw.tagline_zh ?? fallback.taglineZh),
    taglineEn: String(raw.taglineEn ?? raw.tagline_en ?? fallback.taglineEn),
    configVersion:
      raw.configVersion === undefined && raw.config_version === undefined
        ? undefined
        : toNumber(raw.configVersion ?? raw.config_version),
    updatedBy:
      raw.updatedBy !== undefined
        ? String(raw.updatedBy)
        : raw.updated_by !== undefined
          ? String(raw.updated_by)
          : undefined,
    updatedAt:
      raw.updatedAt !== undefined
        ? String(raw.updatedAt)
        : raw.updated_at !== undefined
          ? String(raw.updated_at)
          : undefined,
  };
}

export function normalizeFaq(raw: unknown): FaqEntry {
  if (!isRecord(raw)) {
    return {
      questionZh: '',
      questionEn: '',
      answerZh: '',
      answerEn: '',
      visible: true,
    };
  }

  return {
    id: raw.id === undefined ? undefined : String(raw.id),
    questionZh: String(raw.questionZh ?? raw.question_zh ?? ''),
    questionEn: String(raw.questionEn ?? raw.question_en ?? ''),
    answerZh: String(raw.answerZh ?? raw.answer_zh ?? ''),
    answerEn: String(raw.answerEn ?? raw.answer_en ?? ''),
    visible:
      typeof raw.visible === 'boolean'
        ? raw.visible
        : typeof raw.is_visible === 'boolean'
          ? raw.is_visible
          : true,
    sortOrder:
      raw.sortOrder === undefined && raw.sort_order === undefined
        ? undefined
        : toNumber(raw.sortOrder ?? raw.sort_order),
  };
}

export function normalizeBundle(raw: CopyFaqResponse | unknown): CopyFaqBundle {
  const source = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;

  if (!isRecord(source)) {
    return getDefaultBundle();
  }

  return {
    copy: normalizeCopy(source.copy),
    faqs: ensureArray<unknown>(source.faqs).map(normalizeFaq),
  };
}

export function toDraftFaqs(faqs: FaqEntry[]): FaqDraft[] {
  return faqs.map((item, index) => ({
    ...item,
    localId: item.id ? `server-${item.id}` : `local-${Date.now()}-${index}`,
  }));
}

export function createEmptyFaq(index: number): FaqDraft {
  return {
    localId: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    questionZh: '',
    questionEn: '',
    answerZh: '',
    answerEn: '',
    visible: true,
    sortOrder: index,
  };
}

export function buildPayload(copy: EventCopy, faqs: FaqDraft[]): CopyFaqPayload {
  return {
    copy: {
      bannerZh: copy.bannerZh.trim(),
      bannerEn: copy.bannerEn.trim(),
      taglineZh: copy.taglineZh.trim(),
      taglineEn: copy.taglineEn.trim(),
    },
    faqs: faqs.map((item) => ({
      questionZh: item.questionZh.trim(),
      questionEn: item.questionEn.trim(),
      answerZh: item.answerZh.trim(),
      answerEn: item.answerEn.trim(),
      visible: Boolean(item.visible),
    })),
  };
}

export function isBundleEqual(
  leftCopy: EventCopy,
  leftFaqs: FaqDraft[],
  rightCopy: EventCopy,
  rightFaqs: FaqDraft[]
) {
  return (
    JSON.stringify(buildPayload(leftCopy, leftFaqs)) ===
    JSON.stringify(buildPayload(rightCopy, rightFaqs))
  );
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;

    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
